<template>
  <!-- Admin address dialog -->
  <Teleport to="body">
    <div
      v-if="isEditModalVisible"
      class="modal-backdrop"
      @click="isEditModalVisible = false"
    />
    <div v-if="isEditModalVisible" class="modal-sm">
      <div class="modal-header">
        <span>Ajouter une adresse</span>
        <button class="close-btn" @click="isEditModalVisible = false">✕</button>
      </div>
      <div
        class="modal-body"
        style="display: flex; flex-direction: column; gap: 12px"
      >
        <input
          v-model="newAddress"
          class="field-input"
          type="text"
          placeholder="Ex: 123 Rue de la Paix, 75001 Paris"
          @keydown.enter="doAddAddress"
        />
        <button
          class="btn btn-primary"
          style="width: 100%"
          :disabled="!newAddress || isLoading"
          @click="doAddAddress"
        >
          Ajouter →
        </button>
      </div>
    </div>
  </Teleport>

  <!-- List row -->
  <a
    class="list-row"
    :href="event.eventLink"
    target="_blank"
    @click.prevent.stop="openDetail"
  >
    <!-- Date -->
    <div class="lr-date">
      <div class="d">{{ startDay }}</div>
      <div class="m">{{ startMonth }}</div>
    </div>

    <!-- Name + source -->
    <div>
      <div class="lr-name">{{ event.name }}</div>
      <div class="lr-sub">{{ SOURCES[event.source] }}</div>
    </div>

    <!-- Location -->
    <div class="lr-loc">
      {{ cityName
      }}<span
        v-if="event.departementCode || event.zipCode"
        style="color: var(--ink-dim)"
      >
        ({{ event.departementCode || event.zipCode.substring(0, 2) }})</span
      >
    </div>

    <!-- Type -->
    <EventTypeBadge :type="event.type" />

    <!-- Tags -->
    <div class="lr-tags">
      <span class="tag">{{ event.duration }}J</span>
      <button
        v-if="event.incompleteAddress && event.type === 'onSite' && user"
        class="tag"
        style="
          cursor: pointer;
          border-color: var(--warn);
          color: var(--warn);
          background: none;
        "
        @click.prevent.stop="isEditModalVisible = true"
      >
        + Adresse
      </button>
    </div>

    <!-- CTA -->
    <div class="lr-cta">→</div>
  </a>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import type { EventItem } from '@/types/Event';
import { API_ENDPOINTS } from '@/config/api';

const SOURCES: Record<string, string> = {
  scoringFit: 'Scoring Fit',
  competitionCorner: 'Comp. Corner',
  manual: 'Manuel',
  hyrox: 'Hyrox',
  maboxdecross: 'Ma Box de Cross',
};

const user = useSupabaseUser();
const { fetchWithAuth } = useApiConfig();
const { add: notify } = useNotification();
const {
  proxy: { gtag },
} = useScriptGoogleAnalytics();

const emit = defineEmits(['refreshEventsList']);

const props = defineProps<{ event: EventItem }>();

const isEditModalVisible = ref(false);
const newAddress = ref('');
const isLoading = ref(false);

useEscapeKey(isEditModalVisible, () => {
  isEditModalVisible.value = false;
});

const startDjs = computed(() => dayjs.utc(props.event.startDate));
const endDjs = computed(() => dayjs.utc(props.event.endDate));

const startDay = computed(() => startDjs.value.format('DD'));
const startMonth = computed(() =>
  startDjs.value.format('MMM').toUpperCase().replace('.', ''),
);
const isMultiDay = computed(() => !startDjs.value.isSame(endDjs.value, 'day'));

const cityName = computed(() => {
  if (props.event.type === 'online') return 'En ligne';
  return props.event.city;
});

const doAddAddress = async () => {
  if (!newAddress.value) return;
  try {
    isLoading.value = true;
    await fetchWithAuth(API_ENDPOINTS.EVENT_ADD_ADDRESS(props.event._id), {
      method: 'PUT',
      body: { newAddress: newAddress.value },
    });
    isEditModalVisible.value = false;
    newAddress.value = '';
    emit('refreshEventsList');
  } catch {
    notify("Adresse introuvable, veuillez vérifier l'adresse saisie", 'error');
  } finally {
    isLoading.value = false;
  }
};

const { open: openDetailPanel } = useEventDetail();

const openDetail = () => {
  gtag('event', 'open_event', {
    event_category: 'engagement',
    event_label: props.event.name,
  });
  openDetailPanel(props.event);
};
</script>
